import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import UserService from "../users/UserService.js";
import { currentTenant, noofusers, roles, userid } from "../Common/Credentials.js";
import { appmasterid } from "../Common/Permissions.js";
import { spin } from "../Common/NewLoader.jsx";
import MasterService from "../Masters/MasterService.js";
import Swal from "sweetalert2";
import moment from "moment";
import { OSSwitch } from "../NewUI/NewComponents/OSSwitch.jsx";

import EditIcon from "../images/Icons/edit.png";
import DeleteIcon from "../images/Icons/delete.png";
import { Tooltip } from "@mui/material";
import Breadcrumb from "../NewUI/NewComponents/Breadcrumb.jsx";

const UsersDataTable = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [hoveredAction, setHoveredAction] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [noOfUsersLimit, setNoOfUsersLimit] = useState(noofusers);
  const [sortConfig, setSortConfig] = useState({ key: 'username', direction: 'asc' });
  const navigate = useNavigate();

  // Search highlighting methods
  const escapeRegex = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const highlightSearchText = (text, searchTerm) => {
    if (!searchTerm.trim() || !text) return text;

    const regex = new RegExp(`(${escapeRegex(searchTerm)})`, 'gi');
    const parts = String(text).split(regex);

    return parts.map((part, index) =>
      regex.test(part)
        ? React.createElement('mark', {
          key: index,
          className: 'UI-search-highlight'
        }, part)
        : part
    );
  };

  // Handle resize
  const handleResize = () => {
    const mobile = window.innerWidth <= 768;
    setIsMobile(mobile);
    if (!mobile) {
      setShowSearch(false);
    }
  };

  const refreshUsers = useCallback(() => {
    UserService.retriveAllUsers()
      .then((response) => {
        const sortedUsers = response.data.sort((a, b) => a.username?.localeCompare(b.username));
        setUsers(sortedUsers);
        setFilteredUsers(sortedUsers);
      })
      .finally(() => {
        setLoading(false);
      });

    MasterService.GetNoofUsersLinit(currentTenant)
      .then((res) => {
        setNoOfUsersLimit(res.data || noofusers);
      });
  }, [currentTenant]);

  useEffect(() => {
    if (window.innerWidth <= 768) {
      setShowSearch(true);
    }
    window.addEventListener('resize', handleResize);

    if (!userid) {
      window.confirm("Your Session is Lost: Please login Back to resume");
      navigate("/");
      return;
    }

    setLoading(true);
    refreshUsers();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [navigate, refreshUsers]);

  useEffect(() => {
    updateFilteredData();
  }, [users, searchTerm]);

  // Sorting function
  const sortUsers = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedUsers = (users) => {
    if (!sortConfig.key) return users;

    return [...users].sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.key) {
        case 'username':
          aValue = a.username || '';
          bValue = b.username || '';
          break;
        case 'name':
          aValue = `${a.first || ''} ${a.last || ''}`;
          bValue = `${b.first || ''} ${b.last || ''}`;
          break;
        case 'email':
          aValue = a.email || '';
          bValue = b.email || '';
          break;
        case 'roles':
          aValue = a.roles?.[0]?.name || '';
          bValue = b.roles?.[0]?.name || '';
          break;
        case 'status':
          aValue = a.active || '';
          bValue = b.active || '';
          break;
        default:
          aValue = a[sortConfig.key] || '';
          bValue = b[sortConfig.key] || '';
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const updateFilteredData = () => {
    let filtered;
    if (!searchTerm.trim()) {
      filtered = users;
    } else {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = users.filter(user =>
        user.username?.toLowerCase().includes(lowerSearchTerm) ||
        user.first?.toLowerCase().includes(lowerSearchTerm) ||
        user.last?.toLowerCase().includes(lowerSearchTerm) ||
        user.email?.toLowerCase().includes(lowerSearchTerm)
      );
    }
    setFilteredUsers(filtered);
    setCurrentPage(1);
  };

  // Get current page data for pagination
  const getCurrentPageData = () => {
    const sortedUsers = getSortedUsers(filteredUsers);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return sortedUsers.slice(indexOfFirstItem, indexOfLastItem);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const deleteUser = (id) => {
    if (id === userid) {
      Swal.fire({
        title: "Cannot Delete",
        text: "You cannot delete your own account.",
        icon: "warning",
        confirmButtonText: "Ok",
        confirmButtonColor: "#152f5f",
      });
      return;
    }

    Swal.fire({
      title: "Delete User?",
      text: "Are you sure?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes",
      confirmButtonColor: "#152f5f",
      cancelButtonText: "No",
      reverseButtons: true,
    }).then((result) => {
      if (result.value) {
        UserService.deleteUser(id)
          .then(() => {
            Swal.fire({
              title: "User Deleted Successfully!",
              text: ".",
              icon: "success",
              confirmButtonText: "Ok",
              confirmButtonColor: "#152f5f",
            }).then(() => {
              setLoading(true);
              refreshUsers();
            });
          })
          .catch((e) => {
            if (e.response?.data?.error === "Unauthorized") {
              Swal.fire({
                title: "Your Session is Lost: Please login Back to resume",
                text: ".",
                icon: "error",
                confirmButtonText: "Ok",
                confirmButtonColor: "#152f5f",
              }).then(() => navigate("/"));
            }
          });
      }
    });
  };

  const ActiveorInactive = (id) => {
    if (id === userid) {
      Swal.fire({
        title: "Cannot Modify",
        text: "You cannot modify your own account status.",
        icon: "warning",
        confirmButtonText: "Ok",
        confirmButtonColor: "#152f5f",
      });
      return;
    }

    const userdetails = users.find(e => e.id === id);
    Swal.fire({
      title: `Make user as ${userdetails.active === "INACTIVE" ? "Active" : "Inactive"}?`,
      text: "Are you sure?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes",
      confirmButtonColor: "#152f5f",
      cancelButtonText: "No",
      reverseButtons: true,
    }).then((result) => {
      if (result.value) {
        UserService.ActiveInActiveUser(id)
          .then(() => {
            Swal.fire({
              title: `User ${userdetails.active === "INACTIVE" ? "Activated" : "Inactivated"} Successfully!`,
              text: ".",
              icon: "success",
              confirmButtonText: "Ok",
              confirmButtonColor: "#152f5f",
            }).then(() => {
              setLoading(true);
              refreshUsers();
            });
          })
          .catch((e) => {
            if (e.response?.data?.error === "Unauthorized") {
              Swal.fire({
                title: "Your Session is Lost: Please login Back to resume",
                text: ".",
                icon: "error",
                confirmButtonText: "Ok",
                confirmButtonColor: "#152f5f",
              }).then(() => navigate("/"));
            }
          });
      }
    });
  };

  const Resetpassword = (id) => {
    Swal.fire({
      title: "Reset Password",
      text: "Do you want to reset password?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      confirmButtonColor: '#152f5f',
      cancelButtonText: "No",
    }).then((result) => {
      if (result.value) {
        UserService.Resetpassword(id)
          .then(() => {
            Swal.fire({
              title: "Password Reset Successfull",
              icon: "success",
              confirmButtonText: "Ok",
              confirmButtonColor: '#152f5f',
            }).then(() => {
              refreshUsers();
            });
          });
      }
    });
  };

  // Render pagination controls
  const renderPagination = () => {
    if (filteredUsers.length <= itemsPerPage) return null;

    const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

    return (
      <div className={`UI-pagination-container ${isMobile ? 'UI-pagination-container-mobile' : ''}`}>
        <div className={`UI-pagination-info ${isMobile ? 'UI-pagination-info-mobile' : ''}`}>
          Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredUsers.length)} of {filteredUsers.length} users
        </div>
        <div className={`UI-pagination-controls ${isMobile ? 'UI-pagination-controls-mobile' : ''}`}>
          <select
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            className={`UI-page-size-select ${isMobile ? 'UI-page-size-select-mobile' : ''}`}
          >
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
          </select>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === 1 ? 'UI-pagination-button-disabled' : ''} ${hovered === 'first' && currentPage !== 1 ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            onMouseEnter={() => !(currentPage === 1) && setHovered('first')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'First' : 'First'}
          </button>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === 1 ? 'UI-pagination-button-disabled' : ''} ${hovered === 'prev' && currentPage !== 1 ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            onMouseEnter={() => !(currentPage === 1) && setHovered('prev')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Prev' : 'Previous'}
          </button>

          <span className={`UI-pagination-info ${isMobile ? 'UI-pagination-info-mobile' : ''}`}>
            Page {currentPage} of {totalPages}
          </span>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === totalPages ? 'UI-pagination-button-disabled' : ''} ${hovered === 'next' && currentPage !== totalPages ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            onMouseEnter={() => !(currentPage === totalPages) && setHovered('next')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Next' : 'Next'}
          </button>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === totalPages ? 'UI-pagination-button-disabled' : ''} ${hovered === 'last' && currentPage !== totalPages ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            onMouseEnter={() => !(currentPage === totalPages) && setHovered('last')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Last' : 'Last'}
          </button>
        </div>
      </div>
    );
  };

  // Sort indicator component
  const SortIndicator = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <i className="fa fa-sort UI-sort-icon" />;
    }
    return sortConfig.direction === 'asc'
      ? <i className="fa fa-sort-up UI-sort-icon UI-sort-active" />
      : <i className="fa fa-sort-down UI-sort-icon UI-sort-active" />;
  };

  const renderTable = () => {
    const currentPageData = getCurrentPageData();

    if (filteredUsers.length === 0) {
      return (
        <div className={`UI-no-data-message ${isMobile ? 'UI-no-data-message-mobile' : ''}`}>
          {searchTerm ? 'No users found matching your search' : 'No users available'}
        </div>
      );
    }

    return (
      <>
        <div className={`UI-table-container ${isMobile ? 'UI-table-container-mobile' : ''}`}>
          <table className={`UI-table ${isMobile ? 'UI-table-mobile' : ''}`}>
            <thead className="UI-table-header">
              <tr>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'username' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortUsers('username')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    User Name
                    <SortIndicator columnKey="username" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'name' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortUsers('name')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Name
                    <SortIndicator columnKey="name" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'email' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortUsers('email')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Email
                    <SortIndicator columnKey="email" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'roles' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortUsers('roles')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Role
                    <SortIndicator columnKey="roles" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'status' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortUsers('status')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Status
                    <SortIndicator columnKey="status" />
                  </div>
                </th>
                {(appmasterid === "2" || appmasterid === "3") && (
                  <th className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} UI-th-actions`}>Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {currentPageData.map((user) => (
                <tr
                  key={user.id}
                  className={`UI-table-row ${hoveredRow === user.id ? 'UI-table-row-hovered' : ''} ${isMobile ? 'UI-table-row-mobile' : ''} ${user.active === "ACTIVE" ? 'UI-row-active' : 'UI-row-inactive'}`}
                  onMouseEnter={() => setHoveredRow(user.id)}
                  onMouseLeave={() => {
                    setHoveredRow(null);
                    setHoveredAction(null);
                  }}
                >
                  {/* User Name Cell */}
                  <td
                    className={`UI-td UI-user-name ${hoveredRow === user.id ? 'UI-user-name-hover' : ''} ${isMobile ? 'UI-td-mobile' : ''}`}
                  >
                    {highlightSearchText(user.username, searchTerm)}
                  </td>

                  {/* Name Cell */}
                  <td
                    className={`UI-td ${hoveredRow === user.id ? 'UI-item-name-hover' : ''} ${isMobile ? 'UI-td-mobile' : ''}`}
                  >
                    {highlightSearchText(`${user.first} ${user.last}`, searchTerm)}
                  </td>

                  {/* Email Cell */}
                  <td
                    className={`UI-td UI-email ${hoveredRow === user.id ? 'UI-item-name-hover' : ''} ${isMobile ? 'UI-td-mobile' : ''}`}
                  >
                    {highlightSearchText(user.email, searchTerm)}
                  </td>

                  {/* Role Cell */}
                  <td
                    className={`UI-td UI-role ${hoveredRow === user.id ? 'UI-item-name-hover' : ''} ${isMobile ? 'UI-td-mobile' : ''}`}
                  >
                    {user.roles?.map((role, index) => (
                      <div key={index} className="UI-role-badge">
                        {role.name}
                      </div>
                    ))}
                  </td>

                  {/* Status Cell */}
                  <td className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}>
                    {user.id !== userid ? (
                      <Tooltip title={`Click to ${user.active === "ACTIVE" ? "deactivate" : "activate"} user`} arrow slotProps={{
                        popper: {
                          sx: {
                            pointerEvents: 'none',
                          },
                        },
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'start' }}>
                          <OSSwitch
                            checked={user.active === "ACTIVE"}
                            onChange={() => ActiveorInactive(user.id)}
                          />
                        </div>
                      </Tooltip>
                    ) : (
                      <Tooltip title="You cannot modify your own status" arrow>
                        <span className="UI-status-na">CURRENT USER</span>
                      </Tooltip>
                    )}
                  </td>

                  {/* Actions Cell */}
                  {(appmasterid === "2" || appmasterid === "3") && (
                    <td className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}>
                      <div className="UI-action-buttons" style={{ gap: 0 }}>
                        {/* Edit User */}
                        {user.id !== userid && <Tooltip title="Edit User" arrow slotProps={{
                          popper: {
                            sx: { pointerEvents: 'none' },
                          },
                        }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate("/newusers", { state: { id: user.id, name: "Update User" } });
                            }}
                            className={`UI-action-button ${hoveredAction === `edit-${user.id}` ? 'UI-edit-button-hover' : ''} ${isMobile ? 'UI-action-button-mobile' : ''}`}
                            onMouseEnter={() => setHoveredAction(`edit-${user.id}`)}
                            onMouseLeave={() => setHoveredAction(null)}
                          >
                            <img
                              src={EditIcon}
                              alt="Edit"
                              className={`UI-action-icon ${isMobile ? 'UI-action-icon-mobile' : ''} ${hoveredAction === `edit-${user.id}` ? 'UI-action-icon-hover' : ''}`}
                            />
                          </button>
                        </Tooltip>}

                        {/* Delete User */}
                        {user.id !== userid && <Tooltip title="Delete User" arrow slotProps={{
                          popper: {
                            sx: { pointerEvents: 'none' },
                          },
                        }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteUser(user.id);
                            }}
                            className={`UI-action-button ${hoveredAction === `delete-${user.id}` ? 'UI-delete-button-hover' : ''} ${isMobile ? 'UI-action-button-mobile' : ''}`}
                            onMouseEnter={() => setHoveredAction(`delete-${user.id}`)}
                            onMouseLeave={() => setHoveredAction(null)}
                          >
                            <img
                              src={DeleteIcon}
                              alt="Delete"
                              className={`UI-action-icon ${isMobile ? 'UI-action-icon-mobile' : ''} ${hoveredAction === `delete-${user.id}` ? 'UI-action-icon-hover' : ''}`}
                            />
                          </button>
                        </Tooltip>}

                        {/* Reset Password */}
                        {(roles === "ROLE_SUPERADMIN" || roles === "ADMIN") && <Tooltip title="Reset Password" arrow slotProps={{
                          popper: {
                            sx: { pointerEvents: 'none' },
                          },
                        }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate("/changepassword", { state: user });
                            }}
                            className={`UI-action-button ${hoveredAction === `reset-${user.id}` ? 'UI-reset-button-hover' : ''
                              } ${isMobile ? 'UI-action-button-mobile' : ''}`}
                            onMouseEnter={() => setHoveredAction(`reset-${user.id}`)}
                            onMouseLeave={() => setHoveredAction(null)}
                          >
                            <i className="fas fa-key UI-reset-icon" style={{ fontSize: "18px" }} />
                          </button>
                        </Tooltip>}

                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {renderPagination()}
      </>
    );
  };

  const canAddNewUser = () => {
    return (noOfUsersLimit === null || noOfUsersLimit === 0 || users.length < noOfUsersLimit);
  };

  return (
    <div className={`UI-container ${isMobile ? 'UI-container-mobile' : ''}`}>
      <div className="UI-card">
        {/* Header */}
        <div className={`UI-header ${isMobile ? 'UI-header-mobile' : ''}`}>
        <Breadcrumb breadcrumbs={[{name:"Home",path:"/masters"},{name:"Users",path:null}]}/>
          {/* <nav className={`UI-breadcrumb ${isMobile ? 'UI-breadcrumb-mobile' : ''}`}>
            <Link
              to="/masters"
              className={`UI-breadcrumb-item ${hovered === 'home' ? 'UI-breadcrumb-item-hover' : ''}`}
              onMouseEnter={() => setHovered('home')}
              onMouseLeave={() => setHovered(null)}
            >
              Home
            </Link>
            <span>/</span>
            <span className="UI-breadcrumb-item UI-breadcrumb-active">
              Users
            </span>
          </nav> */}

          <div className={`UI-header-content ${isMobile ? 'UI-header-content-mobile' : ''}`}>
            <div>
              <h1 className={`UI-title ${isMobile ? 'UI-title-mobile' : ''}`}>
                Users
              </h1>
              <p className={`UI-subtitle ${isMobile ? 'UI-subtitle-mobile' : ''}`}>
                Manage user accounts and permissions
              </p>
            </div>

            <div className={`UI-header-actions ${isMobile ? 'UI-header-actions-mobile' : ''}`}>
              {/* Mobile Search */}
              {isMobile && (
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="UI-mobile-search-input"
                />
              )}

              <div className={`UI-buttons-row ${isMobile ? 'UI-buttons-row-mobile' : ''}`}>
                {/* Desktop Search Toggle */}
                {!isMobile && (
                  <div
                    className={`UI-search-container ${showSearch ? 'UI-search-container-expanded' : ''} ${hovered === "search" ? 'UI-search-container-hover' : ''}`}
                    onClick={() => {
                      if (!showSearch) setShowSearch(true);
                    }}
                    onMouseEnter={() => setHovered("search")}
                    onMouseLeave={() => setHovered(null)}
                  >
                    <i
                      className="fa fa-search UI-search-icon"
                    />
                    {!showSearch && "Search"}
                    {showSearch && (
                      <input
                        type="text"
                        placeholder="Search users..."
                        value={searchTerm}
                        onChange={(e) => handleSearch(e.target.value)}
                        className="UI-search-input"
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                        onBlur={() => {
                          if (searchTerm.trim() === "") {
                            setShowSearch(false);
                          }
                        }}
                      />
                    )}
                  </div>
                )}

                {/* New User button */}
                {(appmasterid === "2" || appmasterid === "3") && canAddNewUser() && (
                  <Link
                    to="/newusers"
                    state={{ id: -1, name: "New User" }}
                    className={`UI-btn UI-btn-primary ${hovered === "new" ? 'UI-btn-primary-hover' : ''} ${isMobile ? 'UI-btn-mobile' : ''}`}
                    onMouseEnter={() => setHovered("new")}
                    onMouseLeave={() => setHovered(null)}
                  >
                    <i className="fa fa-plus" /> {isMobile ? "New" : "New User"}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="UI-loading-container">
            {spin}
          </div>
        ) : (
          <>
            {renderTable()}
            {!canAddNewUser() && (
              <div className="UI-limit-warning">
                <i className="fa fa-exclamation-triangle"></i>
                Note: Users limit reached
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default UsersDataTable;